import { checkIsObjEmpty } from '@repo/internal-tools-ts-utils/src/lib/checkIsObjEmpty.ts'
import { checkIsObjShallowEqual } from '@repo/internal-tools-ts-utils/src/lib/checkIsObjShallowEqual.ts'
import type { UnknownObj } from '@repo/internal-tools-type-utils/src/primitives.ts'
import type {
  HasRequiredKeys,
  RequiredBy,
} from '@repo/internal-tools-type-utils/src/utils.ts'
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import {
  type AgentTraits,
  type AnalyticsAdapter,
  type AnalyticsConfig,
  type AnalyticsPageViewProperties,
  type AnalyticsTrackProperties,
} from './analyticsAdapter.ts'
import type { SharedAnalyticsEvents } from './sharedAnalyticsEvents.ts'

type ViewportProperties = {
  viewportHeight?: number
  viewportWidth?: number
}

const getViewportProperties = (): ViewportProperties => {
  if (typeof window === 'undefined') return {}

  return {
    viewportHeight: window.innerHeight,
    viewportWidth: window.innerWidth,
  }
}

export type AnalyticsEventsMap = Record<string, UnknownObj<string>>

export type AnalyticsEventName<Events extends AnalyticsEventsMap> = Extract<
  keyof Events,
  string
>

export type AnalyticsContextValue<
  Events extends AnalyticsEventsMap = SharedAnalyticsEvents,
> = {
  /**
   * Identify the current **agent** and store the agentId in analytics context.
   * After this is called once, other calls (setAgentTraits/track) don't need agentId.
   */
  setAgent: (agentId: string, traitsOrBuilder?: AgentTraitsOrBuilder) => void
  /**
   * Update agent properties for the current agent (no agentId required).
   * If called before setAgent(), traits are queued and flushed on setAgent().
   */
  setAgentTraits: (traitsOrBuilder: AgentTraitsOrBuilder) => void
  /**
   * Track an event (auto-attaches `appId` + `appVersion` + `viewportWidth` + `viewportHeight`).
   */
  track: <EventName extends AnalyticsEventName<Events>>(
    event: EventName,
    ...args: HasRequiredKeys<Events[EventName]> extends true
      ? [properties: AnalyticsPropertiesOrBuilder<Events[EventName]>]
      : [properties?: AnalyticsPropertiesOrBuilder<Events[EventName]>]
  ) => void
  /**
   * Track a screen view (auto-attaches `appId` + `appVersion` + `viewportWidth` + `viewportHeight`).
   */
  pageView: AnalyticsAdapter['pageView']
  /**
   * Track an event once per session by a stable key (prevents spam).
   */
  trackOnce: <EventName extends AnalyticsEventName<Events>>(
    params: AnalyticsTrackOnceParams<EventName, Events[EventName]>,
  ) => void
  /**
   * Reset agent identity + clear any queued traits / trackOnce cache.
   */
  resetAgent: () => void
}

type AnyAnalyticsContextValue = AnalyticsContextValue<AnalyticsEventsMap>

const AnalyticsContext = createContext<AnyAnalyticsContextValue | undefined>(
  undefined,
)

export type AnalyticsTraitsContext = {
  /**
   * App identifier to prevent collisions across apps.
   * Example: 'ccc-main-app' | 'ccc-case-app' | 'ccc-queue-app'
   */
  appId: string
  /**
   * App version (use ENV_APP_VERSION from each app).
   */
  appVersion: string
}

export interface AnalyticsProviderProps
  extends Pick<AnalyticsTraitsContext, 'appId' | 'appVersion'> {
  children: ReactNode
  adapter: AnalyticsAdapter
  config: AnalyticsConfig
}

export type AgentTraitsBuilder = (ctx: AnalyticsTraitsContext) => AgentTraits

export type AgentTraitsOrBuilder = AgentTraits | AgentTraitsBuilder

export type AnalyticsPropertiesBuilder<
  Properties extends UnknownObj<string> = AnalyticsTrackProperties,
> = (ctx: AnalyticsTraitsContext) => Properties

export type AnalyticsPropertiesOrBuilder<
  Properties extends UnknownObj<string> = AnalyticsTrackProperties,
> = Properties | AnalyticsPropertiesBuilder<Properties>

export type AnalyticsTrackOnceParams<
  EventName extends string = string,
  Properties extends UnknownObj<string> = AnalyticsTrackProperties,
> = {
  event: EventName
  key: string
  propsComparator?: (
    prevProps: AnalyticsTrackProperties | undefined,
    nextProps: AnalyticsTrackProperties | undefined,
  ) => boolean
} & (HasRequiredKeys<Properties> extends true
  ? { properties: AnalyticsPropertiesOrBuilder<Properties> }
  : { properties?: AnalyticsPropertiesOrBuilder<Properties> })

export type AnalyticsTrackOnceParamsWithRequiredProperties<
  EventName extends string = string,
  Properties extends UnknownObj<string> = AnalyticsTrackProperties,
> = RequiredBy<AnalyticsTrackOnceParams<EventName, Properties>, 'properties'>

export const AnalyticsProvider = (props: AnalyticsProviderProps) => {
  const { children, appId, appVersion, adapter, config } = props

  // Initialize adapter when it changes.
  useEffect(() => {
    adapter.initialize(config)
  }, [adapter, config])

  const currentAgentIdRef = useRef<string | undefined>(undefined)
  /**
   * Trait deltas that were set before we know the current agentId.
   *
   * Once an agentId is available, we flush these deltas (in order) to the adapter and clear them.
   * Adapters are expected to merge traits across `identify()` calls (Segment does).
   */
  const pendingTraitsRef = useRef<AgentTraits[]>([])
  const trackOncePropsCacheRef = useRef(
    new Map<string, AnalyticsTrackProperties | undefined>(),
  )

  const contextValue = useMemo((): AnyAnalyticsContextValue => {
    const traitsContext = { appId, appVersion } satisfies AnalyticsTraitsContext

    const resolveTraits = (
      traitsOrBuilder: AgentTraitsOrBuilder | undefined,
    ): AgentTraits | undefined => {
      if (!traitsOrBuilder) return undefined

      const traits =
        typeof traitsOrBuilder === 'function'
          ? traitsOrBuilder(traitsContext)
          : traitsOrBuilder

      return checkIsObjEmpty(traits) ? undefined : traits
    }

    const resolveTrackProperties = (
      propertiesOrBuilder:
        | AnalyticsPropertiesOrBuilder<UnknownObj<string>>
        | undefined,
    ): AnalyticsTrackProperties | undefined => {
      if (!propertiesOrBuilder) return undefined

      const resolvedProperties =
        typeof propertiesOrBuilder === 'function'
          ? propertiesOrBuilder(traitsContext)
          : propertiesOrBuilder

      return checkIsObjEmpty(resolvedProperties)
        ? undefined
        : resolvedProperties
    }

    const trackWithContext = (
      eventName: string,
      propertiesOrBuilder?: AnalyticsPropertiesOrBuilder<UnknownObj<string>>,
    ) => {
      const resolvedProperties = resolveTrackProperties(propertiesOrBuilder)

      adapter.track(eventName, {
        ...resolvedProperties,
        appId,
        appVersion,
        ...getViewportProperties(),
      })
    }

    const pageViewWithContext = (
      screenName: string,
      properties?: AnalyticsPageViewProperties,
    ) => {
      adapter.pageView(screenName, {
        ...properties,
        appId,
        appVersion,
        ...getViewportProperties(),
      })
    }

    return {
      setAgent: (agentId, traitsOrBuilder) => {
        const previousAgentId = currentAgentIdRef.current
        const isChangingAgents =
          typeof previousAgentId === 'string' && previousAgentId !== agentId

        // If the agent changes within the same browser session, clear queued traits + per-session caches.
        // This prevents leaking the previous agent's traits or trackOnce state to the next agent.
        if (isChangingAgents) {
          pendingTraitsRef.current = []
          trackOncePropsCacheRef.current.clear()
          adapter.reset()
        }

        currentAgentIdRef.current = agentId

        const traits = resolveTraits(traitsOrBuilder)

        // Flush any queued deltas first (so the initial traits passed here win on conflicts,
        // matching the previous merge behavior: { ...queued, ...initial }).
        const queuedDeltas = pendingTraitsRef.current
        pendingTraitsRef.current = []
        for (const delta of queuedDeltas) {
          adapter.identify(agentId, delta)
        }

        // Identify agent (base identity + optional initial traits).
        // Note: We intentionally do not auto-prefix traits in the provider.
        if (traits) {
          adapter.identify(agentId, traits)

          // If there are no queued deltas and initial traits are empty,
          // we still need to call identify.
          // This prevents calling identify with nothing if there are queued deltas
          // and identify would have already been called.
        } else if (queuedDeltas.length === 0) {
          adapter.identify(agentId, undefined)
        }
      },
      setAgentTraits: (traitsOrBuilder) => {
        const agentId = currentAgentIdRef.current

        const traits = resolveTraits(traitsOrBuilder)
        if (!traits) return

        if (!agentId) {
          // No agent yet: queue until `setAgent()` is called.
          pendingTraitsRef.current.push(traits)
          return
        }

        // Agent exists: forward only the delta; adapters should merge across identify() calls.
        adapter.identify(agentId, traits)
      },
      track: trackWithContext,
      pageView: pageViewWithContext,
      trackOnce: <EventName extends string>(
        params: AnalyticsTrackOnceParams<EventName, UnknownObj<string>>,
      ) => {
        const {
          event,
          key,
          properties,
          propsComparator = checkIsObjShallowEqual,
        } = params

        const propsCache = trackOncePropsCacheRef.current
        const cacheKey = `${event}::${key}`
        const hasPrev = propsCache.has(cacheKey)
        const prevProps = propsCache.get(cacheKey)
        const nextProps = resolveTrackProperties(properties)

        if (hasPrev && propsComparator(prevProps, nextProps)) return

        propsCache.set(
          cacheKey,
          // Clone the props to avoid the original obj being mutated and affecting the cache.
          nextProps ? { ...nextProps } : undefined,
        )
        trackWithContext(event, nextProps)
      },
      resetAgent: () => {
        currentAgentIdRef.current = undefined
        pendingTraitsRef.current = []
        trackOncePropsCacheRef.current.clear()
        adapter.reset()
      },
    }
  }, [adapter, appId, appVersion])

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  )
}

/**
 * Access analytics context if available.
 *
 * This is useful for "optional analytics" hooks that should not throw
 * when a component is rendered outside an `AnalyticsProvider` (e.g. Storybook).
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalAnalytics = <
  Events extends AnalyticsEventsMap = SharedAnalyticsEvents,
>(): AnalyticsContextValue<Events> | undefined => {
  return useContext(AnalyticsContext)
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAnalytics = <
  Events extends AnalyticsEventsMap = SharedAnalyticsEvents,
>(): AnalyticsContextValue<Events> => {
  const context = useContext(AnalyticsContext) as
    | AnalyticsContextValue<Events>
    | undefined
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider')
  }
  return context
}
